import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RotateCcw, Sparkles, Clock } from "lucide-react";
import { fmtBusinessDate } from "@/lib/timezone";

export interface PastBooking {
  id: string;
  service_category: string;
  service_name: string;
  booking_date: string;
  addonNames: string[];
}

export interface ResolvedService {
  category: any;
  sub: any;
}

/** Match a past booking back to a currently-offered category + sub-service. */
export const resolveRebookService = (services: any[], b: { service_category: string; service_name: string }): ResolvedService | null => {
  const baseName = b.service_name.split(" + ")[0].trim().toLowerCase();
  const byTitle = services.find((c) => c.title === b.service_category);
  const findSub = (c: any) =>
    c.subServices.find((s: any) => s.name.toLowerCase() === baseName) ||
    c.subServices.find((s: any) => b.service_name.toLowerCase().startsWith(s.name.toLowerCase()));
  if (byTitle) {
    const sub = findSub(byTitle);
    if (sub) return { category: byTitle, sub };
  }
  for (const c of services) {
    const sub = findSub(c);
    if (sub) return { category: c, sub };
  }
  return null;
};

interface Props {
  userId: string;
  /** Currently offered service categories — used to hide services no longer bookable. */
  services: any[];
  /** Called when the client confirms a rebook. wantsChanges = they want to edit service/add-ons first. */
  onRebook: (booking: PastBooking, wantsChanges: boolean) => void;
}

const RebookBar = ({ userId, services, onRebook }: Props) => {
  const [past, setPast] = useState<PastBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<PastBooking | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("bookings")
        .select("id, service_category, service_name, booking_date, status").is("deleted_at", null)
        .eq("user_id", userId)
        .in("status", ["completed", "confirmed"])
        .order("booking_date", { ascending: false })
        .limit(20);

      if (cancelled) return;
      const rows = data || [];

      // Add-ons booked alongside each appointment
      const ids = rows.map((r) => r.id);
      let addonMap: Record<string, string[]> = {};
      if (ids.length) {
        const { data: addons } = await supabase
          .from("booking_addons")
          .select("booking_id, name, status")
          .in("booking_id", ids);
        (addons || [])
          .filter((a) => a.status === "confirmed")
          .forEach((a) => {
            addonMap[a.booking_id] = [...(addonMap[a.booking_id] || []), a.name];
          });
      }

      // De-duplicate by service, keep most recent 3
      const seen = new Set<string>();
      const unique: PastBooking[] = [];
      for (const r of rows) {
        const key = `${r.service_category}|${r.service_name}`;
        if (seen.has(key)) continue;
        if (!resolveRebookService(services, r)) continue;
        seen.add(key);
        unique.push({
          id: r.id,
          service_category: r.service_category,
          service_name: r.service_name,
          booking_date: r.booking_date,
          addonNames: addonMap[r.id] || [],
        });
        if (unique.length === 3) break;
      }
      setPast(unique);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, services]);

  if (loading || past.length === 0) return null;

  return (
    <>
      <div className="mb-10 border border-primary/30 bg-primary/5 p-5">
        <div className="flex items-center gap-2 mb-4">
          <RotateCcw size={14} className="text-primary" />
          <h2 className="text-xs tracking-[0.25em] uppercase font-medium text-primary">
            Welcome back — rebook in one tap
          </h2>
        </div>
        <div className="space-y-2">
          {past.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => setPending(b)}
              className="w-full flex items-center justify-between gap-3 border border-border/60 bg-background hover:border-primary transition-colors p-3 text-left"
            >
              <div className="min-w-0">
                <p className="font-serif italic text-base truncate">{b.service_name}</p>
                <p className="text-[11px] tracking-wider uppercase text-muted-foreground truncate">
                  {b.service_category}
                  {b.addonNames.length > 0 && ` · ${b.addonNames.length} add-on${b.addonNames.length === 1 ? "" : "s"}`}
                </p>
              </div>
              <span className="shrink-0 flex items-center gap-1 text-[11px] tracking-wider uppercase text-muted-foreground">
                <Clock size={12} /> {fmtBusinessDate(b.booking_date, { month: "short", day: "numeric" })}
              </span>
            </button>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground font-light">
          Skip the service steps and jump straight to picking your time.
        </p>
      </div>

      <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent className="bg-card border-border/50 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif italic text-2xl flex items-center gap-2">
              <Sparkles size={16} className="text-primary" />
              Any changes today?
            </DialogTitle>
            <DialogDescription className="text-sm font-light">
              You're rebooking <span className="text-foreground">{pending?.service_name}</span>
              {pending?.addonNames.length ? (
                <> with {pending.addonNames.join(", ")}</>
              ) : null}
              . Would you like to make any changes or add-ons?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              className="rounded-none border-primary/30 hover:bg-primary/10 tracking-[0.2em] uppercase text-xs font-medium"
              onClick={() => {
                const b = pending!;
                setPending(null);
                onRebook(b, true);
              }}
            >
              Yes, edit
            </Button>
            <Button
              className="rounded-none tracking-[0.2em] uppercase text-xs font-medium"
              onClick={() => {
                const b = pending!;
                setPending(null);
                onRebook(b, false);
              }}
            >
              No, pick a time
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RebookBar;
